import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import '../styles/ImageLightbox.css';

const ImageLightbox = ({ imageUrl, alt, onClose }) => {
  const overlayRef = useRef(null);
  const imageRef = useRef(null);
  
  // Transform state
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [showZoomLevel, setShowZoomLevel] = useState(false);
  
  // Touch tracking refs (using refs to avoid stale closures)
  const initialDistance = useRef(null);
  const initialScale = useRef(1);
  const lastTouchMidpoint = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const zoomIndicatorTimeout = useRef(null);

  // Show zoom indicator and auto-hide after delay
  const showZoomIndicator = useCallback(() => {
    setShowZoomLevel(true);
    
    // Clear existing timeout
    if (zoomIndicatorTimeout.current) {
      clearTimeout(zoomIndicatorTimeout.current);
    }
    
    // Hide after 1 second
    zoomIndicatorTimeout.current = setTimeout(() => {
      setShowZoomLevel(false);
    }, 1000);
  }, []);

  // Calculate distance between two touch points
  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Calculate midpoint between two touch points
  const getMidpoint = (touches) => {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  };

  // Handle touch start
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      // Pinch start
      e.preventDefault();
      initialDistance.current = getDistance(e.touches);
      initialScale.current = scale;
      lastTouchMidpoint.current = getMidpoint(e.touches);
      isDragging.current = false;
    } else if (e.touches.length === 1 && scale > 1) {
      // Single finger drag (only when zoomed in)
      isDragging.current = true;
      dragStart.current = {
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      };
    }
  }, [scale, position]);

  // Handle touch move
  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && initialDistance.current) {
      // Pinch zoom
      e.preventDefault();
      
      const currentDistance = getDistance(e.touches);
      const currentMidpoint = getMidpoint(e.touches);
      
      // Calculate new scale
      const scaleMultiplier = currentDistance / initialDistance.current;
      let newScale = initialScale.current * scaleMultiplier;
      newScale = Math.max(1, Math.min(5, newScale)); // Clamp between 1x and 5x
      
      // Calculate position adjustment to zoom toward pinch center
      if (newScale > 1) {
        const deltaX = currentMidpoint.x - lastTouchMidpoint.current.x;
        const deltaY = currentMidpoint.y - lastTouchMidpoint.current.y;
        
        setPosition(prev => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY
        }));
        
        lastTouchMidpoint.current = currentMidpoint;
      } else {
        // Reset position when fully zoomed out
        setPosition({ x: 0, y: 0 });
      }
      
      setScale(newScale);
      showZoomIndicator();
      
    } else if (e.touches.length === 1 && isDragging.current && scale > 1) {
      // Pan while zoomed
      e.preventDefault();
      
      const newX = e.touches[0].clientX - dragStart.current.x;
      const newY = e.touches[0].clientY - dragStart.current.y;
      
      // Limit panning based on zoom level
      const maxPan = (scale - 1) * 150;
      
      setPosition({
        x: Math.max(-maxPan, Math.min(maxPan, newX)),
        y: Math.max(-maxPan, Math.min(maxPan, newY))
      });
    }
  }, [scale, showZoomIndicator]);

  // Handle touch end
  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length < 2) {
      initialDistance.current = null;
      
      // If scale is close to 1, snap back
      if (scale < 1.1) {
        setScale(1);
        setPosition({ x: 0, y: 0 });
      }
    }
    
    if (e.touches.length === 0) {
      isDragging.current = false;
    }
  }, [scale]);

  // Double tap to zoom
  const lastTap = useRef(0);
  const handleTap = useCallback((e) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;
    
    if (now - lastTap.current < DOUBLE_TAP_DELAY) {
      // Double tap detected
      e.preventDefault();
      
      if (scale > 1) {
        // Zoom out
        setScale(1);
        setPosition({ x: 0, y: 0 });
      } else {
        // Zoom in to 2.5x at tap location
        setScale(2.5);
        showZoomIndicator();
      }
    }
    
    lastTap.current = now;
  }, [scale, showZoomIndicator]);

  // Close on escape key and lock body scroll
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Store current scroll position
    const scrollY = window.scrollY;
    
    // Lock body scroll - use fixed positioning to prevent iOS bounce
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      
      // Clear zoom indicator timeout
      if (zoomIndicatorTimeout.current) {
        clearTimeout(zoomIndicatorTimeout.current);
      }
      
      // Restore body scroll
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, [onClose]);

  // Close when clicking overlay (but not the image)
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  return (
    <div 
      className="image-lightbox-overlay" 
      ref={overlayRef}
      onClick={handleOverlayClick}
    >
      {/* Image container */}
      <div 
        className="lightbox-image-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleTap}
      >
        {/* Close button - positioned relative to image container */}
        <button 
          className="lightbox-close-btn"
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering tap zoom
            onClose();
          }}
          aria-label="Close image"
        >
          <X size={24} strokeWidth={2.5} />
        </button>

        {/* Zoom level indicator */}
        {showZoomLevel && scale > 1 && (
          <div className="lightbox-zoom-level">
            {scale.toFixed(1)}x
          </div>
        )}

        <img 
          ref={imageRef}
          src={imageUrl} 
          alt={alt || "Comic panel full view"}
          className="lightbox-image"
          draggable={false}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transition: isDragging.current ? 'none' : 'transform 0.1s ease-out'
          }}
        />
      </div>
    </div>
  );
};

export default ImageLightbox;